/**
 * SortableChildList — V2 restyle of the v1 `SortableItemList`
 * (src/components/Common/SortableItemList.js).
 *
 *   <SortableChildList
 *     items={[{ id: '12SESSION', label: 'Releasing Anxiety', value: '12' }, …]}
 *     sortOrder={['12SESSION', '15SESSION', …]}
 *     onRemove={(index) => …}
 *     onSortEnd={(newOrder) => …}
 *     showSwitches              // shows featured + dark-mode toggles per row
 *     sessionData={{ '12': { is_featured: 1, is_dark_mode: 0 } }}
 *     onSessionDataChange={(id, next) => …}
 *     emptyText="No sessions yet — add them from the Sessions list."
 *   />
 *
 * Layout per row (V2 chrome):
 *   • Drag handle on the left (six-dot icon, cursor: grab)
 *   • Label takes the flex middle
 *   • Optional Featured + Dark mode compact toggles
 *   • Remove (×) icon button on the right, coral on hover
 *
 * Uses dnd-kit (already a dep) for the sortable wiring — same
 * `arrayMove` semantics as v1 so `onSortEnd` is byte-compatible.
 */
import React, { useCallback } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

/* ─── Drag handle icon (six dots, matches the prototype's
 *  `.card-drag` glyph the deck-card-item uses) ──────────────── */

const DragGlyph = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 14, height: 14 }}>
    <circle cx="9" cy="6" r="0.5" />
    <circle cx="9" cy="12" r="0.5" />
    <circle cx="9" cy="18" r="0.5" />
    <circle cx="15" cy="6" r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

const RemoveGlyph = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

/* ─── Compact toggle (matches SettingsCard recipe) ─────────── */

function CompactToggle({ checked, onChange, label }) {
  return (
    <label
      className="inline-flex items-center cursor-pointer select-none"
      style={{ gap: 6, flexShrink: 0 }}
      title={label}
    >
      <span style={{ position: 'relative', width: 30, height: 18, display: 'inline-block' }}>
        <input
          type="checkbox"
          checked={!!checked}
          onChange={onChange}
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        />
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 999,
            background: checked ? 'rgb(var(--color-teal-green))' : 'rgb(var(--color-sand))',
            transition: 'background 160ms ease',
          }}
        />
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 14 : 2,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: '#FFFFFF',
            boxShadow: '0 1px 3px #2C3E5040',
            transition: 'left 160ms ease',
          }}
        />
      </span>
      <span
        className="text-text-muted"
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          fontFamily: 'var(--font-condensed)',
        }}
      >
        {label}
      </span>
    </label>
  );
}

/* ─── Single sortable row ────────────────────────────────── */

function SortableRow({
  itemId,
  item,
  index,
  onRemove,
  showSwitches,
  onSessionDataChange,
  sessionData,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: itemId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // The v1 row keys sessionData by the numeric ID stripped from
  // `itemId` (e.g. '12SESSION' → '12'). Mirror that so the
  // featured/dark-mode flags route to the same key the submit
  // handler reads. Falls back to `item.value` if the strip yields
  // nothing (e.g. when the id has no digits).
  const numericId = itemId.replace(/[^0-9]/g, '') || item?.value || '';
  const row = sessionData?.[numericId] || {};

  // Audiobook-style hierarchy hints (all optional — callers that
  // don't set `item.kind` get the default flat row treatment, so
  // every existing usage keeps working). Mirrors how
  // TheTappingSolutionWeb's `AudiobookSessionItem` renders:
  //   kind === 'chapter' → full-width, bolder title, no indent
  //   kind === 'session' → indented from the left, thumbnail +
  //                        small uppercase eyebrow above the title
  const kind = item?.kind;
  const isChapter = kind === 'chapter';
  const isSession = kind === 'session';
  const indentLeft = isSession ? 28 : 0;
  // Optional: why the app does not list this row (e.g. "Inactive").
  const hiddenReason = item?.hiddenReason || null;

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, marginLeft: indentLeft }}
      className="flex items-center"
    >
      <div
        className="flex items-center"
        style={{
          flex: 1,
          gap: 10,
          padding: isChapter ? '10px 12px' : '8px 12px',
          background: isChapter ? 'rgb(var(--color-cream))' : 'rgb(var(--color-surface))',
          border: `1px solid ${isChapter ? '#D6CCAE' : 'rgb(var(--color-sand))'}`,
          borderRadius: 8,
          marginBottom: 6,
          transition: 'border-color 160ms ease, box-shadow 160ms ease',
          minWidth: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#D6CCAE';
          e.currentTarget.style.boxShadow = '0 1px 3px #2C3E5012';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = isChapter ? '#D6CCAE' : 'rgb(var(--color-sand))';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {/* Drag handle */}
        <button
          type="button"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
          className="grid place-items-center"
          style={{
            width: 22,
            height: 22,
            border: 0,
            background: 'transparent',
            color: '#9CA1A8',
            cursor: 'grab',
            touchAction: 'none',
            flexShrink: 0,
          }}
        >
          {DragGlyph}
        </button>

        {/* Index */}
        <span
          className="font-mono text-text-muted"
          style={{ fontSize: 11, minWidth: 22, textAlign: 'right' }}
        >
          {String(index + 1).padStart(2, '0')}
        </span>

        {/* Optional session thumbnail (audiobook list, sessions only) */}
        {isSession && item?.imageUrl && (
          <img
            src={item.imageUrl}
            alt=""
            aria-hidden="true"
            style={{
              width: 32,
              height: 32,
              borderRadius: 4,
              objectFit: 'cover',
              flexShrink: 0,
              border: '1px solid rgb(var(--color-sand))',
            }}
          />
        )}

        {/* Label cluster — eyebrow + title.
            • Chapter: bold uppercase title, slightly larger
            • Session: small eyebrow ("TAPPING MEDITATION") + title
            • Default (no kind): plain title (existing behavior) */}
        <div style={{ flex: 1, minWidth: 0, opacity: hiddenReason ? 0.55 : 1 }}>
          {isSession && (
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 9.5, letterSpacing: '0.08em', lineHeight: 1.2 }}
            >
              Tapping meditation
            </div>
          )}
          <span
            className="text-navy"
            style={{
              display: 'block',
              fontSize: isChapter ? 14 : 13,
              fontWeight: isChapter ? 700 : 500,
              letterSpacing: isChapter ? '0.01em' : undefined,
              textTransform: isChapter ? 'uppercase' : undefined,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {item?.label || `Item ${index + 1}`}
          </span>
        </div>

        {hiddenReason && (
          <span
            className="font-condensed font-bold uppercase"
            title="Not listed in the app"
            data-testid="hidden-in-app-badge"
            style={{
              fontSize: 9.5,
              letterSpacing: '0.08em',
              padding: '2px 7px',
              borderRadius: 999,
              background: '#E8735A26',
              color: '#B4533F',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {hiddenReason} · hidden in app
          </span>
        )}

        {/* Small kind badge — chapter only (sessions get the eyebrow
            above the title instead so the badge would duplicate the
            signal). */}
        {isChapter && (
          <span
            className="font-condensed font-bold uppercase"
            style={{
              fontSize: 9.5,
              letterSpacing: '0.08em',
              padding: '2px 7px',
              borderRadius: 999,
              background: '#75A29333',
              color: '#5C7E72',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Chapter
          </span>
        )}

        {/* Per-row toggles */}
        {showSwitches && onSessionDataChange && (
          <>
            <CompactToggle
              checked={row.is_featured}
              label="Featured"
              onChange={(e) =>
                onSessionDataChange(numericId, {
                  ...row,
                  is_featured: e.target.checked ? 1 : 0,
                })
              }
            />
            <CompactToggle
              checked={row.is_dark_mode}
              label="Dark"
              onChange={(e) =>
                onSessionDataChange(numericId, {
                  ...row,
                  is_dark_mode: e.target.checked ? 1 : 0,
                })
              }
            />
          </>
        )}

        {/* Remove */}
        <button
          type="button"
          onClick={() => onRemove(index)}
          aria-label="Remove"
          className="grid place-items-center cursor-pointer"
          style={{
            width: 28,
            height: 28,
            background: 'transparent',
            border: '1px solid transparent',
            borderRadius: '50%',
            color: '#9CA1A8',
            transition: 'all 140ms ease',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#D64545';
            e.currentTarget.style.background = '#D6454514';
            e.currentTarget.style.borderColor = '#D6454540';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#9CA1A8';
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'transparent';
          }}
        >
          {RemoveGlyph}
        </button>
      </div>
    </div>
  );
}

/* ─── Main ─────────────────────────────────────────────── */

export default function SortableChildList({
  items,
  sortOrder,
  onRemove,
  onSortEnd,
  showSwitches = false,
  onSessionDataChange,
  sessionData = {},
  emptyText = 'No items yet.',
}) {
  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = sortOrder.findIndex((id) => id === active.id);
        const newIndex = sortOrder.findIndex((id) => id === over.id);
        const next = arrayMove(sortOrder, oldIndex, newIndex);
        onSortEnd?.(next);
      }
    },
    [sortOrder, onSortEnd],
  );

  if (!sortOrder || sortOrder.length === 0) {
    return (
      <div
        className="text-text-muted"
        style={{
          padding: 16,
          fontSize: 13,
          textAlign: 'center',
          background: 'rgb(var(--color-surface))',
          border: '1px dashed rgb(var(--color-sand))',
          borderRadius: 8,
        }}
      >
        {emptyText}
      </div>
    );
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext items={sortOrder} strategy={verticalListSortingStrategy}>
        {sortOrder.map((id, index) => {
          const item = items?.find((i) => i.id === id);
          return (
            <SortableRow
              key={id}
              itemId={id}
              item={item}
              index={index}
              onRemove={onRemove}
              showSwitches={showSwitches}
              onSessionDataChange={onSessionDataChange}
              sessionData={sessionData}
            />
          );
        })}
      </SortableContext>
    </DndContext>
  );
}
